// select / searchable-select / cascader 弹层的统一 builtinPlacements（自适应翻转）。
//
// ⚠️ 自适应翻转能生效的前提是浮层挂在 document.body（见下方 rteSelectPopupContainer），
// 否则被消费端滚动祖先裁剪、空间算错、方向判反。两件事要一起看：
//
// 1) builtinPlacements 必须配齐 4 个方向：antd `mergedBuiltinPlacements(builtin) =
//    builtin || getBuiltInPlacements()` 是**整体替换**官方默认（不是合并）。早期只给 bottomLeft
//    → antd 没有 topLeft 可翻 → 下方放不下时只能靠 shiftY 把弹层平移上去 → 脱离触发器、误位。
//    故复制官方 4 placement（points/offset 一致），overflow 开 adjustX+adjustY、**关掉 shiftY**
//    ——翻转（adjustY）为主手段、保持锚定触发器；不靠 shiftY 平移（会脱节，与 AgentDropdown 同款取舍）。
//
// 2) 但单配齐 placement 不够（next.77 实测无效）：rc-trigger 的 useAlign 用
//    `collectScroller(popupEle)` 沿**浮层自己**的 DOM 父链收集 overflow 祖先算可视空间，
//    浮层挂 .rte-popup-host（在 .rte-editor 子树内）时会被消费端滚动容器裁剪 → 总朝上。
//    真正解法是把浮层挂 document.body 逃出滚动祖先，此时 adjustY 才按真实视口翻转。详见
//    rteSelectPopupContainer 注释。
const SELECT_OVERFLOW = {
  adjustX: true,
  adjustY: true,
  shiftY: false,
} as const;

// 字段下拉（select / searchable-select / cascader）专用挂载容器：直接挂 document.body。
//
// ⚠️ 为什么不跟其它浮层一样走 ConfigProvider 的 .rte-popup-host：
// rc-trigger 的 useAlign 用 `collectScroller(popupEle)` 沿**浮层自己**的 DOM 父链收集所有
// overflow≠visible 的祖先，作为 getVisibleArea 的裁剪框算「下方可视空间」——**position:fixed
// 拦不住这个纯 JS 的 parentElement 遍历**。.rte-popup-host 虽是 fixed;inset:0 且自身无 overflow，
// 但它住在 .rte-editor 子树里，消费端（首页聊天页）输入框上方有 overflow:auto 的滚动容器
// → 该容器把下方可视空间算小 → rc-align 误判「下方放不下」→ 即使真有空间也翻上
// （用户实测：商品平台/宽高比等下拉框总朝上，4-placement 也救不了，因根因在容器不在 placement）。
//
// 挂 document.body 后浮层父链无任何 overflow 祖先 → getVisibleArea 用真实视口 → adjustY 正常
// 自适应翻转（下够向下、不够才翻上）。这正是 V1 `getPopupContainer={()=>document.body}` 的做法。
// 注：字段下拉（select/searchable-select/cascader）+ 智能体下拉（AgentDropdown）共用此 helper；
// 技能面板/ReportUrl 下拉/Steps 预览等仍挂 host（host 是为规避 Qiankun removeChild 崩溃特意选的）。
// antd 受控 Select/Dropdown 浮层自管卸载，挂 body 不触发手写 createPortal 那类 removeChild 问题。
export const rteSelectPopupContainer = (): HTMLElement => document.body;

export const RTE_SELECT_PLACEMENTS = {
  bottomLeft: {
    points: ['tl', 'bl'] as [string, string],
    offset: [0, 4] as [number, number],
    overflow: SELECT_OVERFLOW,
    htmlRegion: 'visible' as const,
    dynamicInset: true,
  },
  bottomRight: {
    points: ['tr', 'br'] as [string, string],
    offset: [0, 4] as [number, number],
    overflow: SELECT_OVERFLOW,
    htmlRegion: 'visible' as const,
    dynamicInset: true,
  },
  topLeft: {
    points: ['bl', 'tl'] as [string, string],
    offset: [0, -4] as [number, number],
    overflow: SELECT_OVERFLOW,
    htmlRegion: 'visible' as const,
    dynamicInset: true,
  },
  topRight: {
    points: ['br', 'tr'] as [string, string],
    offset: [0, -4] as [number, number],
    overflow: SELECT_OVERFLOW,
    htmlRegion: 'visible' as const,
    dynamicInset: true,
  },
};
